// CSV export of admin analytics. The ONLY safe source is data already on screen — it has passed
// through the backend aggregate() (k-anonymised, band-only), so a CSV built from it cannot leak
// anything the dashboard isn't already showing. Never feed raw results here.
//
// Privacy rules enforced below:
//  - suppressed team rows (teams < k) are excluded
//  - no numeric scores (AdminAnalytics carries bands only — there are no score fields to emit)
//  - spreadsheet formula injection is defused (cells starting = + - @ are quoted)
import type { AdminAnalytics, OrgSummary } from './types';

/** Quote a CSV cell; defuse spreadsheet formula injection on leading = + - @. */
export function csvCell(value: unknown): string {
  let s = value == null ? '' : String(value);
  if (/^[=+\-@]/.test(s)) s = `'${s}`;
  if (/[",\n]/.test(s)) s = `"${s.replace(/"/g, '""')}"`;
  return s;
}

function toCsv(rows: (string | number | undefined)[][]): string {
  return rows.map((r) => r.map(csvCell).join(',')).join('\n');
}

/** The Organisations index as a CSV (portfolio summary — bands + spend, never scores). */
export function organisationsToCsv(orgs: OrgSummary[]): string {
  const header = ['Organisation', 'Sector', 'Status', 'Participants', 'Completed', 'Completion %', 'Avg readiness band', 'Spend USD', 'Budget USD'];
  const body = orgs.map((o) => [
    o.name, o.sector ?? '', o.status, o.participantCount, o.completeCount, o.completionPct,
    o.avgReadiness?.label ?? '', o.spentUsd, o.budgetUsd ?? '',
  ]);
  return toCsv([header, ...body]);
}

/** One org's cohort analytics as a CSV. Suppressed team rows are dropped; bands only. */
export function analyticsToCsv(a: AdminAnalytics, orgName?: string): string {
  const sections: (string | number | undefined)[][] = [];
  sections.push(['FluentAI cohort summary', orgName ?? a.campaignId]);
  sections.push([]);
  sections.push(['KPIs']);
  sections.push(['Participants', a.kpis.participantCount]);
  sections.push(['Completed', a.kpis.completeCount]);
  sections.push(['Completion %', a.kpis.completionPct]);
  sections.push(['Average readiness band', a.kpis.avgReadiness?.label ?? '']);
  sections.push([]);
  sections.push(['Capability radar (band)']);
  for (const r of a.capabilityRadar ?? []) sections.push([r.criterion, r.band?.label ?? '']);
  sections.push([]);
  sections.push(['Readiness by team (suppressed teams excluded)']);
  sections.push(['Team', 'Band', 'Participants']);
  for (const t of (a.readinessByTeam ?? []).filter((t) => !t.suppressed)) {
    sections.push([t.team, t.band?.label ?? '', t.participantCount]);
  }
  sections.push([]);
  sections.push(['Top bottlenecks']);
  for (const b of a.topBottlenecks ?? []) sections.push([b.label, `${b.pct}%`]);
  sections.push([]);
  sections.push(['Policy risk']);
  sections.push(['Compliant', a.policyRisk?.compliant ?? 0]);
  sections.push(['At risk', a.policyRisk?.at_risk ?? 0]);
  sections.push(['Non-compliant', a.policyRisk?.non_compliant ?? 0]);
  return toCsv(sections);
}

/** Trigger a client-side download of CSV text (no dependency, no backend round-trip). */
export function downloadCsv(filename: string, csv: string): void {
  if (typeof document === 'undefined') return;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export function exportOrganisationsCsv(orgs: OrgSummary[]): void {
  downloadCsv('organisations.csv', organisationsToCsv(orgs));
}

export function exportAnalyticsCsv(a: AdminAnalytics, orgName?: string): void {
  downloadCsv(`${(orgName ?? a.campaignId).replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-cohort.csv`, analyticsToCsv(a, orgName));
}
